/**
 * @file ContactPage.js
 * @module src/js/pages/memberZone/ContactPage
 * @description
 * **Page component to display contact information of EDALab members.**
 * - Fetches contact details of all current and graduated members from the `/api/contact` endpoint.
 * - Displays member name, email, phone numbers, and address in a paginated table.
 * - Allows filtering by member category (Post Doctoral, Ph.D., M.S., Graduates).
 * - Includes a search bar to filter members by name, email, phone numbers, or address.
 * - Redirects to the login page if the user is not authenticated.
 *
 * @version
 * <p>
 *  2025-11-25: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import useCheckAuthAndFetch from "../../hooks/useCheckAuthAndFetch";
import CategoryPagination from "../../components/CategoryPagination";
import NumberPagination from "../../components/NumberPagination";
import PagedTable from "../../components/PagedTable";
import SpanARow from "../../components/SpanARow";
import SearchBar from "../../components/SearchBar";
import { MailTo, Tel, TextWithBr } from "../../components/UtilityComponents";

export default function ContactPage() {
    const categories = ["Post Doctoral", "Ph.D.", "M.S.", "Graduates (Ph.D.)", "Graduates (M.S.)"];

    const navigate = useNavigate();
    const checkAuthAndFetch = useCheckAuthAndFetch();
    const [members, setMembers] = useState([]);
    const [membersForCurrentCategory, setMembersForCurrentCategory] = useState([]);
    const [filteredMembers, setFilteredMembers] = useState([]);
    const [currentCategory, setCurrentCategory] = useState("Ph.D.");
    const [currentPage, setCurrentPage] = useState(1);
    const [currentFirstPage, setCurrentFirstPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const headerRef = useRef(null);

    const numPageButtons = 10;
    const numItemsPerPage = 10;

    const membersOnCurrentPage = useMemo(() => {
        if (!filteredMembers || filteredMembers.length === 0) return [];
        const start = (currentPage - 1) * numItemsPerPage;
        const end = currentPage * numItemsPerPage;
        return filteredMembers.slice(start, end);
    }, [filteredMembers, currentPage]);
    
    useEffect(() => {
        async function checkAuthAndFetchData() {
            const data = await checkAuthAndFetch({ request: "/api/contact" });
            setMembers(data || []);
            setLoading(false);
        }
        checkAuthAndFetchData();
    }, [checkAuthAndFetch]);

    useEffect(() => {
        const filtered = members.filter(member => {
            if (currentCategory === "Post Doctoral") {
                return member.group === "student" && member.level === 4;
            } else if (currentCategory === "Ph.D.") {
                return member.group === "student" && member.level === 2;
            } else if (currentCategory === "M.S.") {
                return member.group === "student" && (member.level === 1 || member.level === 3);
            } else if (currentCategory === "Graduates (Ph.D.)") {
                return member.group === "grad" && member.level === 2;
            } else if (currentCategory === "Graduates (M.S.)") {
                return member.group === "grad" && member.level === 1;
            } else {
                return false;
            }
        });
        setMembersForCurrentCategory(filtered);
    }, [members, currentCategory]);

    const categoryPagination = (
        <CategoryPagination
            categories={categories}
            currentCategory={currentCategory}
            setCurrentCategory={setCurrentCategory}
            headerRef={headerRef}
        />
    )

    const numberPagination = (
        <NumberPagination
            length={filteredMembers.length}
            numPageButtons={numPageButtons}
            numItemsPerPage={numItemsPerPage}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            currentFirstPage={currentFirstPage}
            setCurrentFirstPage={setCurrentFirstPage}
            currentCategory={currentCategory}
            headerRef={headerRef}
        />
    )

    // The reason for using useMemo here is to prevent unnecessary re-renders
    // You can find more details in the News.js component
    const filterKeys = useMemo(() => ['name', 'mail', 'phone', 'home_phone', 'address'], []);
    const searchBar = (
        <SearchBar
            data={membersForCurrentCategory}
            setFilteredData={setFilteredMembers}
            setCurrentPage={setCurrentPage}
            setCurrentFirstPage={setCurrentFirstPage}
            currentCategory={currentCategory}
            filterKeys={filterKeys}
        />
    );

    const table = (
        <div className="table-wrapper">
            <table>
                <thead>
                    <tr>
                        <th className='hori-vert-center' width='20%'>Name</th>
                        <th className='hori-vert-center'>E-mail</th>
                        <th className='hori-vert-center'>Cellphone<br />Number</th>
                        <th className='hori-vert-center'>Home<br />Phone<br />Number</th>
                        <th className='hori-vert-center' width='20%'>Address</th>
                    </tr>
                </thead>
                <tbody>
                    <SpanARow
                        colSpan="5"
                        onClick={() => navigate('/member-zone/new-member')}
                        text='👤 Add a new member'
                    />
                    <SpanARow
                        colSpan="5"
                        onClick={() => navigate('/member-zone/profile')}
                        text='👤 Edit your profile'
                    />
                    {filteredMembers.length === 0 && (
                        <SpanARow
                            colSpan="5"
                            text="No members found"
                            loading={loading}
                        />
                    )}
                    {membersOnCurrentPage.map((item, i) => (
                        <tr key={i}>
                            <td className='hori-vert-center'><TextWithBr text={item.name} /></td>
                            <td className='hori-vert-center'><MailTo mail={item.mail} /></td>
                            <td className='hori-vert-center'><Tel tel={item.phone} /></td>
                            <td className='hori-vert-center'><Tel tel={item.home_phone} /></td>
                            <td className='hori-vert-center'>{item.address}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    return (
        <PagedTable
            header="Contact"
            headerRef={headerRef}
            categoryPagination={categoryPagination}
            numberPagination={filteredMembers.length > numItemsPerPage ? numberPagination : null}
            table={table}
            searchBar={searchBar}
        />
    )
}
